import { DashboardShell } from "@/components/dashboard-shell"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { MapPin, Zap, Award } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"

const readinessScores = [
  { name: "Highland Wind Phase 2", score: 8.7, technology: "Onshore Wind", capacity: "150MW" },
  { name: "North Sea Wind Cluster", score: 9.2, technology: "Offshore Wind", capacity: "320MW" },
  { name: "Yorkshire Solar Park", score: 8.1, technology: "Solar", capacity: "95MW" },
  { name: "Thames Estuary Solar", score: 7.9, technology: "Solar", capacity: "75MW" },
  { name: "Welsh Battery Storage", score: 7.4, technology: "Energy Storage", capacity: "50MW" },
  { name: "Bristol Biomass", score: 6.8, technology: "Biomass", capacity: "40MW" },
]

const regionalData = [
  { region: "Scotland", projects: 12, totalCapacity: "850MW", avgScore: 8.4, investment: "£890M" },
  { region: "England", projects: 18, totalCapacity: "1200MW", avgScore: 7.9, investment: "£1.2B" },
  { region: "Wales", projects: 6, totalCapacity: "320MW", avgScore: 7.6, investment: "£345M" },
  { region: "Northern Ireland", projects: 4, totalCapacity: "180MW", avgScore: 7.2, investment: "£198M" },
]

const technologyTrends = [
  { month: "Jan", onshoreWind: 65, offshoreWind: 82, solar: 58, storage: 45 },
  { month: "Feb", onshoreWind: 68, offshoreWind: 85, solar: 62, storage: 48 },
  { month: "Mar", onshoreWind: 70, offshoreWind: 88, solar: 65, storage: 52 },
  { month: "Apr", onshoreWind: 72, offshoreWind: 90, solar: 68, storage: 55 },
  { month: "May", onshoreWind: 75, offshoreWind: 92, solar: 72, storage: 58 },
  { month: "Jun", onshoreWind: 78, offshoreWind: 95, solar: 75, storage: 62 },
]

const technologyDistribution = [
  { name: "Offshore Wind", value: 35, color: "#10b981" },
  { name: "Onshore Wind", value: 28, color: "#3b82f6" },
  { name: "Solar", value: 22, color: "#f59e0b" },
  { name: "Energy Storage", value: 10, color: "#8b5cf6" },
  { name: "Biomass", value: 5, color: "#ec4899" },
]

const scoreDistribution = [
  { range: "9.0-10", count: 8 },
  { range: "8.0-8.9", count: 15 },
  { range: "7.0-7.9", count: 12 },
  { range: "6.0-6.9", count: 5 },
  { range: "< 6.0", count: 3 },
]

export default function Gate2AnalyticsPage() {
  const getScoreColor = (score: number) => {
    if (score >= 8.5) return "text-green-500"
    if (score >= 7) return "text-yellow-500"
    return "text-orange-500"
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 8.5) return "bg-green-500/10 border-green-500/20"
    if (score >= 7) return "bg-yellow-500/10 border-yellow-500/20"
    return "bg-orange-500/10 border-orange-500/20"
  }

  return (
    <DashboardShell>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Gate 2 Analytics</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Gate 2 Analytics</h1>
              <p className="text-muted-foreground">Project readiness analysis and market insights</p>
            </div>
            <div className="flex gap-2">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4 flex items-center gap-3">
                  <Award className="h-8 w-8 text-primary" />
                  <div>
                    <div className="text-2xl font-bold">8.2</div>
                    <div className="text-xs text-muted-foreground">Avg Score</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Tabs defaultValue="readiness" className="w-full">
            <TabsList>
              <TabsTrigger value="readiness">Readiness Scores</TabsTrigger>
              <TabsTrigger value="regional">Regional Analysis</TabsTrigger>
              <TabsTrigger value="tech">Technology Trends</TabsTrigger>
            </TabsList>

            <TabsContent value="readiness" className="mt-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Projects by Readiness</CardTitle>
                    <CardDescription>Highest Gate 2 scores in pipeline</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {readinessScores.map((project, idx) => (
                      <div key={idx} className={`p-3 border rounded-lg ${getScoreBgColor(project.score)}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium mb-1">{project.name}</div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {project.technology}
                              </Badge>
                              <span>{project.capacity}</span>
                            </div>
                          </div>
                          <div className={`text-2xl font-bold ${getScoreColor(project.score)}`}>{project.score}</div>
                        </div>
                        <Progress value={project.score * 10} className="h-1.5" />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Score Distribution</CardTitle>
                    <CardDescription>Projects by readiness range</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        count: {
                          label: "Projects",
                          color: "hsl(var(--primary))",
                        },
                      }}
                      className="h-[300px]"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={scoreDistribution}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="range" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="count" fill="var(--color-count)" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Key Metrics</CardTitle>
                  <CardDescription>Gate 2 analysis overview</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-4">
                    <div className="space-y-2">
                      <div className="text-muted-foreground text-sm">Projects Assessed</div>
                      <div className="text-3xl font-bold">43</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-muted-foreground text-sm">High Readiness</div>
                      <div className="text-3xl font-bold text-green-500">23</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-muted-foreground text-sm">Medium Readiness</div>
                      <div className="text-3xl font-bold text-yellow-500">17</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-muted-foreground text-sm">Low Readiness</div>
                      <div className="text-3xl font-bold text-orange-500">3</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="regional" className="mt-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {regionalData.map((region, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <CardTitle className="text-lg">{region.region}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Projects</div>
                        <div className="text-2xl font-bold">{region.projects}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Total Capacity</div>
                        <div className="text-xl font-medium text-primary">{region.totalCapacity}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Avg Gate 2 Score</div>
                        <div className={`text-xl font-bold ${getScoreColor(region.avgScore)}`}>{region.avgScore}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Total Investment</div>
                        <div className="text-lg font-medium">{region.investment}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Regional Comparison</CardTitle>
                  <CardDescription>Project capacity and investment by region</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {regionalData.map((region, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{region.region}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground">{region.totalCapacity}</span>
                            <span className="font-medium">{region.investment}</span>
                          </div>
                        </div>
                        <Progress value={(region.projects / 40) * 100} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tech" className="mt-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Technology Adoption Trends</CardTitle>
                    <CardDescription>Project development by technology type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        onshoreWind: {
                          label: "Onshore Wind",
                          color: "#3b82f6",
                        },
                        offshoreWind: {
                          label: "Offshore Wind",
                          color: "#10b981",
                        },
                        solar: {
                          label: "Solar",
                          color: "#f59e0b",
                        },
                        storage: {
                          label: "Energy Storage",
                          color: "#8b5cf6",
                        },
                      }}
                      className="h-[300px]"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={technologyTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="offshoreWind"
                            stroke="#10b981"
                            strokeWidth={2}
                            name="Offshore Wind"
                          />
                          <Line
                            type="monotone"
                            dataKey="onshoreWind"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="Onshore Wind"
                          />
                          <Line type="monotone" dataKey="solar" stroke="#f59e0b" strokeWidth={2} name="Solar" />
                          <Line
                            type="monotone"
                            dataKey="storage"
                            stroke="#8b5cf6"
                            strokeWidth={2}
                            name="Energy Storage"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Technology Distribution</CardTitle>
                    <CardDescription>Portfolio breakdown by technology</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        value: {
                          label: "Share",
                          color: "hsl(var(--primary))",
                        },
                      }}
                      className="h-[300px]"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={technologyDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {technologyDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {technologyDistribution.slice(0, 3).map((tech, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4" style={{ color: tech.color }} />
                        <CardTitle className="text-base">{tech.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold mb-2" style={{ color: tech.color }}>
                        {tech.value}%
                      </div>
                      <Progress value={tech.value} className="h-2" style={{ backgroundColor: `${tech.color}33` }} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DashboardShell>
  )
}
